/*
 * File: c:\Users\j.mazat\Desktop\Moby\MobyBuy\mobybuy_backoffice\src\Content\chatgpt\priceList.tsx
 * Project: c:\Users\j.mazat\Desktop\Moby\MobyBuy\mobybuy_backoffice
 * Created Date: Wednesday July 17th 2024
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Wednesday July 17th 2024 11:00:38 am
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2024 Autobuy SAS
 * -----
 */

import { TableContainer, Paper, TableHead, TableRow, TableCell, TableBody, Table, TextField } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import Styles from "Content/Pricing/PriceList/priceList.module.css";
import { TiDelete } from "react-icons/ti";
import translator from "MultiLanguage/TranslationWrapper";
import { PriceListRefType, PrincingTypeEnum } from "Type/pricing.type";

type priceListProps = {
    title: string,
    priceType: PrincingTypeEnum,
    defaultMonth: number[],
    defaultMileage: number[],
    cb: Function,
    editable?: boolean,
    spliting?: (nbCol: number) => void,
    deletePricing?: (type: PrincingTypeEnum, month?: number, milage?: number) => void,
    updatePricing?: (value: number, month: number, mileage: number, type: PrincingTypeEnum) => void,
    setup?: PriceListRefType
};

const priceList = forwardRef((props: priceListProps, ref) => {
    const [months, setMonths] = useState(props.defaultMonth);
    const [mileage, setMileage] = useState(props.defaultMileage);
    const tarif = useRef<PriceListRefType>({});

    useEffect(() => {
        if (props.setup && Object.values(props.setup).length > 0) {
            const tmp: number[] = [];
            Object.values(props.setup).forEach(val => Object.keys(val).forEach(x => tmp.push(parseInt(x))));
            setMonths([...new Set(tmp)]);
            setMileage(Object.keys(props.setup).map(x => parseInt(x)));
            tarif.current = props.setup;
        }
    }, [props.setup]);

    useImperativeHandle(ref, () => {
        return tarif.current;
    }, [tarif.current]);

    const handleInputChange = (mil: number, month: number, value: number) => {
        if (props.updatePricing)
            props.updatePricing(value, month, mil, props.priceType);

        if (isNaN(value)) {
            if (tarif.current[mil]) {
                delete tarif.current[mil][month];
                if (Object.values(tarif.current[mil]).length == 0)
                    delete tarif.current[mil];
            }
            return;
        }
        if (!tarif.current[mil])
            tarif.current[mil] = {};
        tarif.current[mil][month] = value;
        tarif.current = {...tarif.current}
    };

    const handleAddMilage = (val: number) => {
        if (props.spliting)
            props.spliting(mileage.length + 1);
        if (isNaN(val) || mileage.includes(val))
            return;

        const newVal = [...mileage, val];
        setMileage(newVal.sort((a, b) => a-b));
    };

    const handleAddMonth = (val: number) => {
        if (isNaN(val) || months.includes(val))
            return;

        const newVal = [...months, val];
        setMonths(newVal.sort((a, b) => a-b));
    };

    const deleteMileage = (val: number) => {
        const tmp = mileage.filter(x => x !== val);
        delete tarif.current[val];
        setMileage(tmp);
        if (props.deletePricing)
            props.deletePricing(props.priceType, undefined, val);
    };

    const deleteMonth = (val: number) => {
        const tmp = months.filter(x => x !== val);
        Object.keys(tarif.current).forEach(x => {
            delete tarif.current[parseInt(x)][val];
        });
        setMonths(tmp);
        if (props.deletePricing)
            props.deletePricing(props.priceType, val);
    };


    const mileageRow = useMemo(() => {
        return mileage.map(x => <TableCell key={Math.random()} align="center" >
                {x} {translator.decode("table.headers.MileageShort")}
                {
                    props.editable ?
                    <TiDelete fill="red" onClick={() => deleteMileage(x)} />
                    : null
                }
            </TableCell>)
    }, [mileage]);

    return (
        <TableContainer elevation={1} component={Paper}>
            <Table >
                <TableHead>
                    <TableRow>
                        <TableCell><strong>{props.title}</strong></TableCell>
                            {mileageRow}
                            { props.editable ?
                                <TableCell sx={{padding: "0px !important"}} >
                                    <TextField className={`${Styles.inputNumber}`} size="small" type="number" onBlur={(e) => {handleAddMilage(parseInt(e.target.value)); e.target.value = ""}} />
                                </TableCell>
                                : null
                            }
                    </TableRow>
                </TableHead>
                <TableBody>
                    {months.map((m) => (
                        <TableRow key={m} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row">
                                {m} {translator.decode("table.time.month")}
                                {
                                    props.editable ?
                                    <TiDelete fill="red" onClick={() => deleteMonth(m)} />
                                    : null
                                }
                            </TableCell>
                            {mileage.map(km =>
                                <TableCell key={Math.random()} align="center">
                                    <TextField disabled={!props.editable} required type="number" size="small"
                                        className={`${Styles.inputNumber}`}
                                        defaultValue={Math.abs(tarif.current[km]?.[m]) || ""}
                                        onBlur={(e) => { handleInputChange(km, m, Math.abs(parseInt(e.target.value))); props.cb();
                                        }}
                                    />
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                    {
                        props.editable ?
                        <TableRow>
                            <TableCell>
                                <TextField className={`${Styles.inputNumber}`} size="small" type="number" onBlur={(e) => {handleAddMonth(parseInt(e.target.value)); e.target.value = ""}} />
                            </TableCell>
                        </TableRow>
                        :null
                    }
                </TableBody>
            </Table>
        </TableContainer>
    )
});

export default priceList;
